import debug from "debug";

/**
 * Centralized logging utility using the `debug` package.
 *
 * Usage:
 * 1. Import the logger: `import { logger } from '@/lib/logger'`
 * 2. Use namespaced loggers: `logger.interview.connection('Connected!', { data })`
 * 3. Create custom loggers: `const myLogger = createLogger('my-feature')`
 *
 * Control logging via localStorage:
 * - Enable all: `localStorage.setItem('debug', 'app:*')`
 * - Enable specific: `localStorage.setItem('debug', 'app:interview:*')`
 * - Enable multiple: `localStorage.setItem('debug', 'app:interview:*,app:api:*')`
 * - Exclude specific: `localStorage.setItem('debug', 'app:*,-app:tracking')`
 * - Disable all: `localStorage.removeItem('debug')`
 *
 * Environment variables:
 * - NEXT_PUBLIC_DEBUG: Set to enable specific namespaces in production
 */

// Auto-enable debug logs based on environment (browser only)
if (typeof window !== "undefined") {
  // In development, enable all logs by default (unless already set)
  if (process.env.NODE_ENV === "development") {
    if (!localStorage.getItem("debug")) {
      debug.enable("app:*");
    }
  }

  // In production, only enable if explicitly set via env var
  if (process.env.NEXT_PUBLIC_DEBUG) {
    debug.enable(process.env.NEXT_PUBLIC_DEBUG);
  }
}

// Create namespaced loggers for different parts of the app
export const logger = {
  // Interview-related logs
  interview: {
    context: debug("app:interview:context"),
    connection: debug("app:interview:connection"),
    session: debug("app:interview:session"),
    audio: debug("app:interview:audio"),
    progress: debug("app:interview:progress"),
    sheet: debug("app:interview:sheet"),
    state: debug("app:interview:state"),
    transcript: debug("app:interview:transcript"),
    auth: debug("app:interview:auth"),
  },

  // Invite flow logs
  invite: {
    page: debug("app:invite:page"),
    state: debug("app:invite:state"),
    auth: debug("app:invite:auth"),
    flow: debug("app:invite:flow"),
  },

  // API logs
  api: {
    request: debug("app:api:request"),
    response: debug("app:api:response"),
    error: debug("app:api:error"),
  },

  // PostHog tracking logs
  tracking: debug("app:tracking"),

  // Authentication logs
  auth: {
    guest: debug("app:auth:guest"),
    middleware: debug("app:auth:middleware"),
    clerk: debug("app:auth:clerk"),
  },

  // Wake lock logs
  wakeLock: debug("app:wake-lock"),

  // Pipecat/RTVI logs
  pipecat: {
    client: debug("app:pipecat:client"),
    transport: debug("app:pipecat:transport"),
    events: debug("app:pipecat:events"),
    messages: debug("app:pipecat:messages"),
  },

  // General app logs
  app: debug("app:general"),
};

/**
 * Helper to create custom namespaced loggers
 * @param namespace - The namespace for your logger (will be prefixed with 'app:')
 * @returns A debug logger instance
 *
 * @example
 * const myLogger = createLogger('my-feature:sub-feature');
 * myLogger('Hello world', { data: 123 });
 * // Enable with: localStorage.setItem('debug', 'app:my-feature:*');
 */
export const createLogger = (namespace: string) => debug(`app:${namespace}`);

/**
 * Re-export debug for advanced usage
 */
export { debug };
